/**
 * Loading Screen — boot overlay shown while AsyncApp loads commits and
 * the lazy editor chunk arrives.
 *
 * Production version: lives in src/client/components/shared so the
 * production app doesn't depend on `docs/tile-ui/` (reference material
 * only).
 *
 * Visual is the `<div class="skeleton">` block from
 * docs/newdesign.html — pulsing tile logo, caption, progress bar, mono
 * sub-caption. CSS lives in src/client/components/shared/loadingScreen.css
 * (a small scoped subset under `.tile-loading-host`).
 *
 * Usage:
 *   1. Controlled — `<LoadingScreen visible={!ready} />`
 *   2. Self-timed — `<LoadingScreen durationMs={2000} onDone={...} />`
 *
 * The wrapper class `.tile-loading-host` is supplied by the caller so we
 * don't double-wrap the DOM. App.tsx and Shell/AppShell.tsx already
 * render `<div className="tile-loading-host">` around <LoadingScreen>.
 */
import React, { useEffect, useState } from 'react';

import Logo from '@/shared/components/logo/Logo';

export interface LoadingScreenProps {
  /** Controlled visibility. When `false` the overlay receives `.hide`
   *  and fades out (`opacity 0`, `pointer-events: none`). Defaults to
   *  `true`. */
  visible?: boolean;
  /** Caption above the progress bar. Default: "Loading editor…". */
  caption?: string;
  /** Mono-font sub-caption. Default: "Hydrating VFS · Indexing AST ·
   *  Connecting session". */
  status?: string;
  /** When set, the overlay auto-fades out after this many ms. Pair with
   *  `onDone` to be notified after the fade completes. */
  durationMs?: number;
  /** Fires 300ms after `visible` flips false (matches the CSS fade-out)
   *  or after `durationMs` elapses in self-timed mode. */
  onDone?: () => void;
  /** Logo width in px. Default 80. */
  logoWidth?: number;
}

export default function LoadingScreen({
  visible: visibleProp,
  caption = 'Loading editor…',
  status = 'Hydrating VFS · Indexing AST · Connecting session',
  durationMs,
  onDone,
  logoWidth = 80,
}: LoadingScreenProps) {
  // If `visible` not provided, default to true and self-time via durationMs.
  const [internalVisible, setInternalVisible] = useState(true);
  const visible = visibleProp ?? internalVisible;

  // Auto-fade after durationMs (only when visibility isn't controlled).
  useEffect(() => {
    if (durationMs == null || visibleProp != null) return;
    const t = setTimeout(() => setInternalVisible(false), durationMs);
    return () => clearTimeout(t);
  }, [durationMs, visibleProp]);

  // Notify after fade-out finishes (matches `.skeleton`'s `transition: .3s`).
  useEffect(() => {
    if (visible || !onDone) return;
    const t = setTimeout(onDone, 300);
    return () => clearTimeout(t);
  }, [visible, onDone]);

  return (
    <div className={`skeleton ${visible ? '' : 'hide'}`} id="skeleton">
      <div className="sk-logo">
        <Logo width={logoWidth} />
      </div>
      <div className="sk-text">{caption}</div>
      <div className="sk-bar">
        <div className="sk-bar-fill" />
      </div>
      <div className="sk-status">{status}</div>
    </div>
  );
}

export { LoadingScreen };
